import { useCallback, useEffect, useRef, useState } from "react";
import Markdown from "react-markdown";

// Fallback seed text. The server is the single source of truth
// (Ai::Coach::Responder::CHIP_SEEDS) and passes the live map via the `chip_seeds`
// prop; this is only used when the prop is absent. The seed is sent as `content`
// so the optimistic user bubble matches what the server persists; `chip` still
// drives intent + mode selection server-side.
const CHIP_SEED = {
  explain: "Why did I get this one wrong?",
  socratic: "Walk me through this — guide me, don't just give me the answer yet.",
  drill: "Give me 3 quick practice questions on this concept.",
  teach: "Teach me the topic behind this question.",
};

const DEFAULT_INTENTS = [
  { key: "explain", label: "Why was I wrong?" },
  { key: "socratic", label: "Walk me through it" },
  { key: "drill", label: "Drill this concept" },
  { key: "teach", label: "Teach me this topic" },
];

export default function AiCoach({
  endpoint,
  attempt_id,
  attempt_kind = "practice",
  csrf_token,
  intents = DEFAULT_INTENTS,
  chip_seeds = CHIP_SEED,
  collapsed = false,
  fill_height = false,
  question_id = null,
  navigator_scoped = false,
  questions = [],
}) {
  const isMock = attempt_kind === "mock";

  const initialQuestionId = navigator_scoped
    ? questions[0]?.question_id ?? null
    : question_id;

  const [open, setOpen] = useState(!collapsed);
  const [currentQuestionId, setCurrentQuestionId] = useState(initialQuestionId);
  const [messages, setMessages] = useState([]);
  const [streamingText, setStreamingText] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [available, setAvailable] = useState(!isMock);
  const [quota, setQuota] = useState(null);
  const [input, setInput] = useState("");

  const scrollRef = useRef(null);

  // Tutor mode: the side panel is shared while questions switch client-side.
  // Track the active question by listening to the exam navigator.
  useEffect(() => {
    if (!navigator_scoped) return undefined;

    const handler = (event) => {
      const index = event.detail?.index ?? 0;
      const next = questions[index]?.question_id;
      if (next) setCurrentQuestionId(next);
    };
    document.addEventListener("exam-navigator:changed", handler);
    return () => document.removeEventListener("exam-navigator:changed", handler);
  }, [navigator_scoped, questions]);

  const loadConversation = useCallback(
    async (qid) => {
      if (!qid || isMock) return;
      setLoading(true);
      setError(null);
      try {
        const url = `${endpoint}?question_id=${encodeURIComponent(qid)}&attempt_id=${encodeURIComponent(attempt_id)}`;
        const res = await fetch(url, { headers: { Accept: "application/json" } });
        if (!res.ok) {
          setAvailable(false);
          return;
        }
        const data = await res.json();
        setMessages(data.messages || []);
        setQuota(data.quota_remaining ?? null);
        setAvailable(Boolean(data.available));
      } catch {
        setError("Couldn't reach the tutor. Check your connection.");
      } finally {
        setLoading(false);
      }
    },
    [endpoint, attempt_id, isMock],
  );

  // Load whenever the panel is open and the active question changes.
  useEffect(() => {
    if (open && currentQuestionId) {
      setMessages([]);
      loadConversation(currentQuestionId);
    }
  }, [open, currentQuestionId, loadConversation]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streamingText]);

  const send = useCallback(
    async ({ chip = null, content = null }) => {
      if (streaming || !currentQuestionId) return;
      const text = content ?? (chip ? chip_seeds[chip] : "");
      if (!text) return;

      setError(null);
      setMessages((prev) => [...prev, { role: "user", content: text }]);
      setStreaming(true);
      setStreamingText("");

      try {
        const res = await fetch(endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrf_token,
            Accept: "text/event-stream",
          },
          body: JSON.stringify({
            question_id: currentQuestionId,
            attempt_id,
            chip,
            content: text,
          }),
        });

        if (!res.ok || !res.body) {
          setError("The tutor is unavailable right now.");
          setStreaming(false);
          return;
        }

        await consumeStream(res.body, {
          onToken: (token) => setStreamingText((prev) => prev + token),
          onDone: (payload) => {
            setStreamingText((finalText) => {
              setMessages((prev) => [...prev, { role: "assistant", content: finalText }]);
              return "";
            });
            if (payload?.quota_remaining != null) setQuota(payload.quota_remaining);
          },
          onError: (payload) => {
            setError(payload?.message || "Something went wrong.");
            setStreamingText("");
          },
        });
      } catch {
        setError("Lost connection to the tutor.");
        setStreamingText("");
      } finally {
        setStreaming(false);
      }
    },
    [streaming, currentQuestionId, endpoint, csrf_token, attempt_id, chip_seeds],
  );

  const handleSubmit = (event) => {
    event.preventDefault();
    const trimmed = input.trim();
    if (!trimmed) return;
    setInput("");
    send({ content: trimmed });
  };

  if (isMock) {
    return (
      <div style={styles.notice}>
        The AI tutor is turned off during mock exams.
      </div>
    );
  }

  if (collapsed && !open) {
    return (
      <button type="button" style={styles.openBtn} onClick={() => setOpen(true)}>
        <i className="fa-solid fa-sparkles" style={{ marginRight: 8 }} />
        Discuss this question with the AI tutor
      </button>
    );
  }

  const panelStyle = fill_height ? { ...styles.panel, height: "100%" } : styles.panel;
  const threadStyle = fill_height
    ? { ...styles.thread, flex: 1, minHeight: 0, maxHeight: "none" }
    : styles.thread;

  return (
    <div style={panelStyle}>
      <div style={styles.header}>
        <span>
          <i className="fa-solid fa-sparkles" style={{ marginRight: 8 }} />
          AI study coach
        </span>
        {quota != null && <span style={styles.quota}>{quota} left today</span>}
      </div>

      <div ref={scrollRef} style={threadStyle}>
        {messages.length === 0 && !streaming && !loading && (
          <p style={styles.hint}>
            Stuck on this one? Ask the coach to explain it, walk you through it, or
            drill the concept.
          </p>
        )}

        {loading && <p style={styles.hint}>Loading…</p>}

        {messages.map((m, i) => (
          <Bubble key={i} role={m.role} text={m.content} />
        ))}

        {streaming && <Bubble role="assistant" text={streamingText || "…"} />}
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {!available ? (
        <div style={styles.notice}>The AI tutor isn't available right now.</div>
      ) : (
        <>
          <div style={styles.chips}>
            {intents.map((intent) => (
              <button
                key={intent.key}
                type="button"
                style={styles.chip}
                disabled={streaming}
                onClick={() => send({ chip: intent.key })}
              >
                {intent.label}
              </button>
            ))}
          </div>

          <form style={styles.form} onSubmit={handleSubmit}>
            <input
              type="text"
              style={styles.input}
              placeholder="Ask a follow-up…"
              value={input}
              disabled={streaming}
              onChange={(e) => setInput(e.target.value)}
              aria-label="Ask the AI tutor a follow-up question"
            />
            <button type="submit" style={styles.send} disabled={streaming || !input.trim()}>
              <i className="fa-solid fa-arrow-right" />
            </button>
          </form>
          <p style={styles.disclaimer}>Study aid only — not medical advice.</p>
        </>
      )}
    </div>
  );
}

function Bubble({ role, text }) {
  const mine = role === "user";
  return (
    <div style={{ ...styles.bubbleRow, justifyContent: mine ? "flex-end" : "flex-start" }}>
      {mine ? (
        <div style={styles.bubbleUser}>{text}</div>
      ) : (
        <div style={styles.bubbleAssistant} className="ai-coach-md">
          <Markdown>{text}</Markdown>
        </div>
      )}
    </div>
  );
}

// Reads a fetch SSE body and dispatches token/done/error events.
async function consumeStream(body, { onToken, onDone, onError }) {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let boundary;
    while ((boundary = buffer.indexOf("\n\n")) !== -1) {
      const block = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      const { event, data } = parseEvent(block);
      if (!data) continue;
      if (event === "token") onToken(data.token || "");
      else if (event === "done") onDone(data);
      else if (event === "error") onError(data);
    }
  }
}

function parseEvent(block) {
  let event = "message";
  const dataLines = [];
  for (const line of block.split("\n")) {
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
  }
  let data = null;
  if (dataLines.length) {
    try {
      data = JSON.parse(dataLines.join("\n"));
    } catch {
      data = null;
    }
  }
  return { event, data };
}

const styles = {
  panel: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    fontFamily: "var(--ui, system-ui)",
    color: "var(--ink-900, #0b1420)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontWeight: 600,
    fontSize: "14px",
  },
  quota: { fontSize: "12px", color: "var(--ink-500, #6b7480)", fontWeight: 500 },
  thread: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    maxHeight: "420px",
    overflowY: "auto",
    paddingRight: "4px",
  },
  hint: { fontSize: "13px", color: "var(--ink-500, #6b7480)", lineHeight: 1.5, margin: 0 },
  bubbleRow: { display: "flex" },
  bubbleUser: {
    background: "var(--accent, #0b6b7a)",
    color: "#fff",
    borderRadius: "12px 12px 2px 12px",
    padding: "8px 12px",
    fontSize: "13px",
    maxWidth: "85%",
    whiteSpace: "pre-wrap",
  },
  bubbleAssistant: {
    background: "var(--surface-50, #f4f6f9)",
    color: "var(--ink-900, #0b1420)",
    borderRadius: "12px 12px 12px 2px",
    padding: "8px 12px",
    fontSize: "13px",
    maxWidth: "90%",
    lineHeight: 1.5,
  },
  chips: { display: "flex", flexWrap: "wrap", gap: "6px" },
  chip: {
    border: "1px solid var(--ink-200, #e3e6ec)",
    background: "var(--paper, #fff)",
    color: "var(--ink-700, #2b3440)",
    borderRadius: "999px",
    padding: "6px 12px",
    fontSize: "12px",
    cursor: "pointer",
    fontFamily: "inherit",
  },
  form: { display: "flex", gap: "6px" },
  input: {
    flex: 1,
    border: "1px solid var(--ink-200, #e3e6ec)",
    borderRadius: "8px",
    padding: "8px 10px",
    fontSize: "13px",
    fontFamily: "inherit",
  },
  send: {
    background: "var(--accent, #0b6b7a)",
    color: "#fff",
    border: 0,
    borderRadius: "8px",
    padding: "0 14px",
    cursor: "pointer",
  },
  openBtn: {
    border: "1px solid var(--ink-200, #e3e6ec)",
    background: "var(--paper, #fff)",
    color: "var(--accent-ink, #0b6b7a)",
    borderRadius: "8px",
    padding: "10px 14px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "var(--ui, system-ui)",
    width: "100%",
  },
  notice: {
    fontSize: "13px",
    color: "var(--ink-500, #6b7480)",
    fontFamily: "var(--ui, system-ui)",
    padding: "8px 0",
  },
  error: {
    fontSize: "12px",
    color: "var(--err, #b3261e)",
    background: "var(--err-tint, #fdeceb)",
    borderRadius: "6px",
    padding: "6px 10px",
  },
  disclaimer: { fontSize: "11px", color: "var(--ink-400, #9aa3ad)", margin: 0 },
};
